import { Arrange, Columns, Tooltip } from "@fullscript/aviary-web";
import {
  faCheckCircle,
  faChevronRight,
  faHeart,
  faInfoCircle,
} from "@fullscript/pro-regular-svg-icons";
import { isAfter, isBefore, subYears } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Typography,
  Spacer,
  Button,
  FloatingLabelInput,
  FloatingLabelSelect,
  PhoneInput,
  Message,
  DatePicker,
  useToast,
  Checkbox,
} from "@aviary";
import { getPhoneWithoutCountryCode, getMinPhoneLength } from "@aviary/components/PhoneInput";
import { isValidPhoneNumberPerCountry } from "@aviary/components/PhoneInput/validatePhoneNumber";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import { isDateOfBirthValid } from "@shared/utils/age/isDateOfBirthValid";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { PasswordInput } from "@unauthenticated/shared/components/PasswordInput/PasswordInput";
import { isPasswordValid } from "@unauthenticated/shared/components/PasswordInput/PasswordValidation/PasswordValidation";
import { usePatientSignUpUnauthenticatedSharedMutation } from "@unauthenticated/shared/data/mutations/PatientSignUp.mutation.generated";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

interface PatientInfoState {
  email: string;
}

interface JourneyPatientInfoFormProps {
  onPatientCreated?: () => void;
  onSubmit?: () => void;
  email?: string;
  storeSlug?: string;
  storeId?: string;
  journeyPackageId?: string;
  patientInfoState?: PatientInfoState;
}

const JourneyPatientInfoForm = ({
  onPatientCreated,
  onSubmit,
  email: initialEmail,
  storeSlug,
  storeId,
  journeyPackageId,
  patientInfoState,
}: JourneyPatientInfoFormProps) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const { makeToast } = useToast();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
  const [biologicalSex, setBiologicalSex] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPregnantOrNursing, setIsPregnantOrNursing] = useState(false);

  const [password, setPassword] = useState("");
  const [passwordDirty, setPasswordDirty] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isMobile = tablet.lessThan;
  const displayEmail = initialEmail || patientInfoState?.email || "";

  const dobISOString =
    dateOfBirth && isDateOfBirthValid(dateOfBirth) ? dateOfBirth.toISOString() : undefined;

  // Build additional attributes for redirect after signup
  const additionalAttributes =
    storeSlug && journeyPackageId
      ? {
          source: "journey_self_checkout", // Required field for patient signup
          redirectPath: `/r/checkout/${storeSlug}/journey/${journeyPackageId}`,
          dateOfBirth: dobISOString,
          sex: biologicalSex || undefined,
          smsNumber: phoneNumber || undefined,
        }
      : {
          source: "journey_self_checkout",
          dateOfBirth: dobISOString,
          sex: biologicalSex || undefined,
          smsNumber: phoneNumber || undefined,
        }; // Always provide source even without redirect

  const [patientSignUp, { loading: isCreatingPatient }] =
    usePatientSignUpUnauthenticatedSharedMutation({
      onCompleted: data => {
        const signUpResult = data?.auth?.patientSignUp;

        if (signUpResult?.errors?.message) {
          makeToast("error", signUpResult.errors.message);

          setIsSubmitting(false); // Reset submitting state on error

          return;
        }

        if (signUpResult?.errors?.fields) {
          const fieldErrors = signUpResult.errors.fields;
          if (fieldErrors.password) {
            makeToast("error", fieldErrors.password[0]);

            setIsSubmitting(false); // Reset submitting state on error

            return;
          }
          // Handle other field errors if they exist
          const firstError = Object.values(fieldErrors)[0];
          if (firstError && Array.isArray(firstError) && firstError.length > 0) {
            makeToast("error", firstError[0]);

            setIsSubmitting(false); // Reset submitting state on error

            return;
          }
        }

        // Success - let backend handle the redirect
        if (signUpResult?.redirectPath) {
          window.location.assign(signUpResult.redirectPath);

          // Don't reset isSubmitting here - keep button disabled until redirect
          return;
        }

        // If no redirect path, call the success callback
        onPatientCreated?.();
        // Don't reset isSubmitting here either - let the parent handle the state
      },
      onError: error => {
        makeToast("error", error?.message || t(l.journeyCheckout.patientInfo.errors.signUpError));
        setIsSubmitting(false); // Reset submitting state on error
      },
    });

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName(e.target.value);
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(e.target.value);
  };

  const handleDateOfBirthChange = (date: Date | null) => {
    setDateOfBirth(date);
  };

  const handleBiologicalSexChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setBiologicalSex(e.target.value);
  };

  const handlePhoneNumberChange = (formattedPhoneNumber: string) => {
    setPhoneNumber(formattedPhoneNumber);
  };

  const handleContinue = () => {
    if (!isFormValid) {
      return;
    }

    onSubmit?.();

    setIsSubmitting(true);

    // Clear any previous reCAPTCHA errors
    setRecaptchaError(false);

    gRecaptchaExecute(
      (captchaToken: string) => {
        patientSignUp({
          variables: {
            input: {
              attributes: {
                firstName,
                lastName,
                email: displayEmail,
                password,
                storeId: storeId || "",
                intake: true, // This is for patient intake
                optional: additionalAttributes,
              },
              captchaToken,
            },
          },
        });
      },
      () => {
        // reCAPTCHA error callback
        setRecaptchaError(true);
        setIsSubmitting(false); // Reset submitting state on reCAPTCHA error
        makeToast("error", t(l.journeyCheckout.patientInfo.errors.recaptchaError));
      }
    );
  };

  const phoneDigits = phoneNumber ? getPhoneWithoutCountryCode(phoneNumber) : "";
  const minPhoneLength = getMinPhoneLength("US");
  const isPhoneValid = phoneDigits && isValidPhoneNumberPerCountry(phoneDigits, minPhoneLength);

  const isFormValid =
    firstName &&
    lastName &&
    dateOfBirth &&
    isDateOfBirthValid(dateOfBirth) &&
    isBefore(dateOfBirth, subYears(new Date(), 18)) &&
    biologicalSex &&
    phoneNumber &&
    isPhoneValid &&
    password &&
    isPasswordValid(password) &&
    storeId &&
    displayEmail &&
    !recaptchaError &&
    !isPregnantOrNursing;

  const dobErrors = useMemo(() => {
    if (!dateOfBirth) {
      return [];
    }

    if (!isDateOfBirthValid(dateOfBirth)) {
      return [t(l.sharedCommon.InvalidDOB)];
    }

    if (isAfter(dateOfBirth, subYears(new Date(), 18))) {
      return [t(l.sharedCommon.DOBMustBeAtLeast18YearsOld)];
    }

    return [];
  }, [dateOfBirth, t]);

  // PhoneInput component handles its own validation internally

  const renderSuccessMessage = () => (
    <Message isColor="success" customIcon={faCheckCircle}>
      <Typography>{t(l.journeyCheckout.patientInfo.journeysAvailable)}</Typography>
    </Message>
  );

  const renderFormFields = () => (
    <div>
      <Columns>
        <Columns.Column columnWidth={6}>
          <FloatingLabelInput
            id="firstName"
            type="text"
            label={t(l.journeyCheckout.patientInfo.firstNameLabel)}
            value={firstName}
            onChange={handleFirstNameChange}
            required
          />
        </Columns.Column>
        <Columns.Column columnWidth={6}>
          <FloatingLabelInput
            id="lastName"
            type="text"
            label={t(l.journeyCheckout.patientInfo.lastNameLabel)}
            value={lastName}
            onChange={handleLastNameChange}
            required
          />
        </Columns.Column>
      </Columns>
      <Columns>
        <Columns.Column columnWidth={6}>
          <DatePicker
            label={t(l.journeyCheckout.patientInfo.dateOfBirthLabel)}
            value={dateOfBirth}
            onChange={handleDateOfBirthChange}
            errors={dobErrors}
            variant={isMobile ? "inline" : "dialog"}
            openTo="year"
            required
          />
        </Columns.Column>
        <Columns.Column columnWidth={6}>
          <FloatingLabelSelect
            id="biologicalSex"
            label={t(l.journeyCheckout.patientInfo.biologicalSexLabel)}
            value={biologicalSex}
            handleChange={handleBiologicalSexChange}
            required
          >
            <option value="">{t(l.journeyCheckout.patientInfo.selectPlaceholder)}</option>
            <option value="female">
              {t(l.journeyCheckout.patientInfo.biologicalSexOptions.female)}
            </option>
            <option value="male">
              {t(l.journeyCheckout.patientInfo.biologicalSexOptions.male)}
            </option>
          </FloatingLabelSelect>
          <Arrange alignment="center" gap="small">
            <Tooltip
              tooltipContent={t(l.journeyCheckout.patientInfo.biologicalSexTooltipText)}
              enterDelay={0}
            >
              <FontAwesomeIcon icon={faInfoCircle} />
            </Tooltip>
            <Typography type="footnote">
              {t(l.journeyCheckout.patientInfo.biologicalSexTooltipLabel)}
            </Typography>
          </Arrange>
        </Columns.Column>
      </Columns>
      <Columns isMarginless>
        <Columns.Column columnWidth={6}>
          <PhoneInput
            id="phoneNumber"
            label={t(l.journeyCheckout.patientInfo.phoneNumberLabel)}
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            required
          />
          <Arrange alignment="center" gap="small">
            <FontAwesomeIcon icon={faInfoCircle} />
            <Typography type="footnote">
              {t(l.journeyCheckout.patientInfo.requiredForLabTesting)}
            </Typography>
          </Arrange>
        </Columns.Column>
        <Columns.Column columnWidth={6}>
          <PasswordInput
            value={password}
            onChange={e => setPassword(e.target.value)}
            label={t(l.journeyCheckout.patientInfo.passwordLabel)}
            isDirty={passwordDirty}
            isLoading={isCreatingPatient}
            onBlur={() => setPasswordDirty(true)}
            required
          />
        </Columns.Column>
      </Columns>
    </div>
  );

  const renderPregnantOrNursingCheckbox = () => {
    if (biologicalSex !== "female") {
      return null;
    }

    return (
      <div>
        <Spacer height="one" />
        <Checkbox
          checked={isPregnantOrNursing}
          onChange={() => setIsPregnantOrNursing(!isPregnantOrNursing)}
        >
          <Typography>{t(l.journeyCheckout.patientInfo.pregnantOrLactatingLabel)}</Typography>
        </Checkbox>
        {!!isPregnantOrNursing && (
          <>
            <Spacer height="oneHalf" />
            <Message isColor="info">
              <Typography>
                {t(l.journeyCheckout.patientInfo.journeysUnavailablePregnantOrLactating)}
              </Typography>
            </Message>
          </>
        )}
      </div>
    );
  };

  const renderContinueButton = () => (
    <Button
      onClick={handleContinue}
      isFullWidth
      size="large"
      isLoading={isCreatingPatient}
      disabled={!isFormValid || isCreatingPatient || isSubmitting}
    >
      {t(l.journeyCheckout.patientInfo.continueButton)}&nbsp;
      <FontAwesomeIcon icon={faChevronRight} />
    </Button>
  );

  const renderAccountMessage = () => (
    <Message isColor="info" customIcon={faHeart}>
      <Typography type="footnote">
        {t(l.journeyCheckout.patientInfo.accountWillBeCreated)}&nbsp;
        <strong>{displayEmail}</strong>.&nbsp;
        {t(l.journeyCheckout.patientInfo.accountBenefits)}
      </Typography>
    </Message>
  );

  return (
    <div>
      {!!isMobile && <Spacer height="one" />}
      {renderSuccessMessage()}
      <Spacer height="one" />
      <Typography type="h2">{t(l.journeyCheckout.patientInfo.title)}</Typography>
      <Spacer height="one" />
      {renderFormFields()}
      {renderPregnantOrNursingCheckbox()}
      <Spacer height="one" />
      {renderContinueButton()}
      <Spacer height="oneHalf" />
      {renderAccountMessage()}
    </div>
  );
};

export { JourneyPatientInfoForm };
